import {createSelector} from 'reselect'
import {combineReducers} from '@modifi/redux'
import {eqBy, path, prop, reject, unionWith, compose} from 'ramda'
import {PAGE_SIZE, REDUCER_KEY} from './constants'
import {openAmountPathMap as paymentOpenAmountPathMap} from '../payments/constants'
import {openAmountPathMap as invoiceOpenAmountPathMap} from '../invoices/constants'
import {
  MATCH_TYPE_INVOICE_DD_RETURN,
  MATCH_TYPE_INVOICE_FUNDS_TRANSFER,
  MATCH_TYPE_INVOICE_REPAYMENT,
  MATCH_TYPE_PAYMENT_FUNDS_TRANSFER,
} from '../match/constants'
import {selectTransactionAmount} from '../../data/transaction'

const ACTION_SET_MATCH_TYPE = 'unmatched-transactions/transaction/SET_MATCH_TYPE'
const ACTION_SET_MATCH_LIST = 'unmatched-transactions/transaction/SET_MATCH_LIST'
const ACTION_CLEAR_MATCH_LIST = 'unmatched-transactions/transaction/CLEAR_MATCH_LIST'
const ACTION_PAGINATION_UPDATE = 'unmatched-transactions/transaction/PAGINATION_UPDATE'
const ACTION_PRE_SET_MATCH_LIST = 'unmatched-transactions/transaction/PRE_SET_MATCH_LIST'
const ACTION_CLEAR_PRE_SET_MATCH_LIST =
  'unmatched-transactions/transaction/CLEAR_PRE_SET_MATCH_LIST'

const initialState = {
  matchType: null,
  matchList: [],
  currentPageIndex: 1,
  currentPageItems: [],
  preSetList: [],
}

const featureReducer = (state = initialState, {type, payload}) => {
  switch (type) {
    case ACTION_SET_MATCH_TYPE: {
      return {
        ...state,
        matchType: payload.matchType,
      }
    }
    case ACTION_PRE_SET_MATCH_LIST: {
      return {
        ...state,
        preSetList: payload.preSetList,
      }
    }
    case ACTION_SET_MATCH_LIST: {
      const {matchList, currentPageIndex} = payload
      return {
        ...state,
        matchList,
        currentPageIndex,
      }
    }
    case ACTION_CLEAR_MATCH_LIST:
      return {
        ...state,
        matchList: initialState.matchList,
      }
    case ACTION_CLEAR_PRE_SET_MATCH_LIST:
      return {
        ...state,
        preSetList: initialState.preSetList,
      }
    case ACTION_PAGINATION_UPDATE: {
      return {
        ...state,
        currentPageIndex: payload,
      }
    }
    default:
      return state
  }
}

export default combineReducers({
  feature: featureReducer,
})

export const aSetMatchType = matchType => dispatch => {
  dispatch({
    type: ACTION_SET_MATCH_TYPE,
    payload: {
      matchType,
    },
  })
}

export const aPreSetMatchList = preSetList => dispatch => {
  dispatch({
    type: ACTION_PRE_SET_MATCH_LIST,
    payload: {
      preSetList,
    },
  })
}

export const selectMatchType = state => path([REDUCER_KEY, 'feature', 'matchType'], state)
export const selectMatchList = state => path([REDUCER_KEY, 'feature', 'matchList'], state)
export const selectPreSetMatchList = state => path([REDUCER_KEY, 'feature', 'preSetList'], state)
export const selectCurrentPageIndex = state =>
  path([REDUCER_KEY, 'feature', 'currentPageIndex'], state)
export const selectMatchListCount = createSelector(selectMatchList, list => list.length)

export const selectListByPageIndex = createSelector(
  selectMatchList,
  selectCurrentPageIndex,
  (list, currentPageIndex) => {
    const itemsInPage = list.reduce((acc, item, index) => {
      const pageIndex = Math.floor(index / PAGE_SIZE) + 1
      acc[pageIndex] = acc[pageIndex] || []
      acc[pageIndex].push(item)
      return acc
    }, {})
    return itemsInPage[currentPageIndex] || []
  }
)

export const aClearPreSetMatchList = () => ({
  type: ACTION_CLEAR_PRE_SET_MATCH_LIST,
})

export const aSetMatchList = (items, canSplit) => (dispatch, getState) => {
  const currentList = selectMatchList(getState())
  const pageIndex = selectCurrentPageIndex(getState())

  // Normalize to an array
  const itemsArray = Array.isArray(items) ? items : [items]

  // If canSplit: toggle each item, otherwise replace entire list
  let matchList
  if (canSplit) {
    // 1) remove any that match incoming items
    const pruned = reject(
      existing => itemsArray.some(i => i.externalId === existing.externalId),
      currentList
    )

    // 2) find new ones to add
    const existingIds = currentList.map(i => i.externalId)
    const toAdd = itemsArray.filter(i => !existingIds.includes(i.externalId))

    // 3) union them (dedup by externalId)
    matchList = unionWith(eqBy(prop('externalId')), pruned, toAdd)
  } else {
    // just replace
    matchList = itemsArray
  }

  // recompute page index like you had before
  const newPageIndex =
    matchList.length > 0 && Math.ceil(matchList.length / PAGE_SIZE) < pageIndex
      ? pageIndex - 1
      : pageIndex

  dispatch({
    type: ACTION_SET_MATCH_LIST,
    payload: {matchList, currentPageIndex: newPageIndex},
  })
}

export const aResetMatchList = () => ({
  type: ACTION_CLEAR_MATCH_LIST,
})

export const aUpdateCurrentPage = payload => ({
  type: ACTION_PAGINATION_UPDATE,
  payload,
})

export const selectInitialFormData = createSelector(
  selectMatchList,
  selectMatchType,
  selectTransactionAmount,
  selectPreSetMatchList,
  (matchList, matchType, transactionAmount, preSetMatchList) => {
    const openAmountPathMap = {...paymentOpenAmountPathMap, ...invoiceOpenAmountPathMap}
    const openAmountPath = path(openAmountPathMap[matchType])

    return matchList.reduce((acc, item) => {
      const preset = preSetMatchList.find(p => p.externalId === item.externalId)
      if (preset && preset.amount !== 0) {
        return {
          ...acc,
          [item.externalId]: preset.amount,
        }
      }
      const openAmount = compose(parseFloat, openAmountPath)(item)
      const formattedAmount =
        matchType === MATCH_TYPE_PAYMENT_FUNDS_TRANSFER ||
        matchType === MATCH_TYPE_INVOICE_DD_RETURN ||
        matchType === MATCH_TYPE_INVOICE_REPAYMENT ||
        matchType === MATCH_TYPE_INVOICE_FUNDS_TRANSFER
          ? openAmount * -1
          : openAmount
      // pre filled value should be the larger amount of transaction amount and open amount
      const amount =
        transactionAmount > 0 && transactionAmount < formattedAmount
          ? transactionAmount
          : formattedAmount
      return {
        ...acc,
        [item.externalId]: amount,
      }
    }, {})
  }
)
